// Image detail (ui-spec §6.11): the detail template over the live Image, one route per tab. A catalog Image opens
// under the nodus project and is read-only; a project Image can be deleted from `⋯`.
import { linkTo, useKObject } from "@nodus/api";
import { cliCommand, formatBytes, usePrefs } from "@nodus/ui";
import { useNavigate } from "@tanstack/react-router";
import { DetailPage } from "../../app/templates";
import { useImageActions } from "./image-actions";
import { BuildTab, OverviewTab } from "./image-tabs";
import { IMAGE_FIXES, imageSource, type Image, type Scope } from "./model";

export const IMAGE_TABS = ["overview", "build"] as const;
export type ImageTab = (typeof IMAGE_TABS)[number];

export function ImageDetail({ scope, name, tab }: { scope: Scope; name: string; tab: ImageTab }) {
  const prefs = usePrefs();
  const navigate = useNavigate();
  const q = useKObject<Image>("Image", name, scope);
  const img = q.object;
  const actions = useImageActions(scope, { onDeleted: () => void navigate({ href: linkTo({ kind: "Image" }, scope) }) });
  const describe = cliCommand(["describe", `image/${name}`], scope, { omitScope: prefs.omitScope });
  const reason = img?.status?.reason;

  return (
    <DetailPage
      kind="Image"
      name={name}
      scope={scope}
      tab={tab}
      crumbs={[{ label: "Images", href: linkTo({ kind: "Image" }, scope) }]}
      loading={q.isLoading}
      error={q.error}
      notFound={q.notFound}
      {...(img ? { object: img } : {})}
      commands={[
        { label: "Describe", cli: describe },
        { label: "Build from a manifest", cli: cliCommand(["apply", "-f", "image.yaml"], scope, { omitScope: prefs.omitScope }) },
        { label: "Delete", cli: cliCommand(["delete", `image/${name}`], scope, { omitScope: prefs.omitScope }) },
      ]}
      header={{
        cli: describe,
        status: { kind: "Image", phase: img?.status?.phase, reason, message: img?.status?.message },
        meta: [img ? imageSource(img) : null, img?.status?.size ? formatBytes(img.status.size) : null, img?.spec?.platform ?? null].filter(Boolean),
        ...(img?.status?.phase === "Failed"
          ? { failure: { reason, message: img.status.message, ...(reason && IMAGE_FIXES[reason] ? { fix: IMAGE_FIXES[reason] } : {}), describeCommand: describe } }
          : {}),
        actions: img ? actions.menu(img) : [],
      }}
    >
      {img ? tab === "build" ? <BuildTab image={img} scope={scope} /> : <OverviewTab image={img} scope={scope} /> : null}
      {actions.dialog}
    </DetailPage>
  );
}
